import React, { useMemo, useState } from "react";
import "../component/productInvoiceHeader.css";

import { FaPlusCircle, FaSearch } from "react-icons/fa";
import { FormLabel, FormInput, Button, Autocomplete } from "../../../components/ui";
import { getInitialDate, toISODateInput } from "../../../helper/formatDate";
import { useDebounce } from "../../../hooks/useDebounce";
import { useSupplierAutocomplete } from "../../../hooks/supplier/useSupplierAutocomplete";

/**
 * @typedef {Object} FilterFormSchema
 * @property {string} invoiceNumber
 * @property {{ id: string, name: string }} distributor
 * @property {string} entryDate
 */

const ProductUnitHeader = ({ onAddProductClicked, onSearchInvoiceNumber }) => {
  /** @type {[FilterFormSchema, React.Dispatch<FilterFormSchema>]} */
  const [forms, setForms] = useState({
    invoiceNumber: '',
    distributor: {
      id: '',
      name: '',
    },
    entryDate: '',
  })

  /** @type {[FilterFormSchema | null, React.Dispatch<FilterFormSchema | null>]} */
  const [formErrors, setFormErrors] = useState(null)

  const [shouldFetchSuggestion, setShouldFetchSuggestion] = useState(false)
  const [selectedSupplierSuggestion, setSelectedSupplierSuggestion] = useState(null)

  const debouncedFormDistributor = useDebounce(forms.distributor.name.toLowerCase())
  const supplierAutocomplete = useSupplierAutocomplete(debouncedFormDistributor, shouldFetchSuggestion)

  const suggestionOptions = useMemo(() => {
    if (!forms.distributor.name) return []
    if (forms.distributor.name === selectedSupplierSuggestion?.label) return []
    return supplierAutocomplete.data.map((item) => ({
      id: item.supplier_id,
      label: item.supplier_name,
    }))
  }, [forms.distributor.name, supplierAutocomplete.data, selectedSupplierSuggestion])

  const handleInputChange = (evt) => {
    const { name, value } = evt.target

    if (name === 'invoiceNumber') {
      handleChangeInvoiceNumber(value)
      return
    }

    if (name === 'distributor') {
      handleChangeSuggestion(value)
      return
    }

    setForms((prevState) => ({
      ...prevState,
      [name]: value
    }))
  }

  function handleChangeInvoiceNumber(value) {
    setFormErrors((prevErrors) => {
      const nextErrors = { ...prevErrors }
      if (value && value.trim().length <= 3) {
        nextErrors.invoiceNumber = "Nomor invoice membutuhkan minimal lebih dari 3 huruf"
      } else {
        delete nextErrors.invoiceNumber
      }
      return nextErrors
    })

    setForms((prevState) => ({
      ...prevState,
      invoiceNumber: value
    }))
  }

  function handleChangeSuggestion(value) {
    setForms((prevState) => ({
      ...prevState,
      distributor: {
        id: '',
        name: value
      }
    }))
    setShouldFetchSuggestion(true)
  }

  function handleSelectSuggestion(suggestionData) {
    setShouldFetchSuggestion(false)
    setSelectedSupplierSuggestion(suggestionData)
    setForms((prevState) => ({
      ...prevState,
      distributor: {
        id: suggestionData.id,
        name: suggestionData.label,
      },
    }))
  }

  const handleSubmitForm = (evt) => {
    evt.preventDefault()
    onSearchInvoiceNumber(forms)
  }

  const hasFormErrors = !!formErrors && Object.keys(formErrors).length > 0

  return (
    <div className="mb-3 py-2">
      <div className="mb-3 flex justify-between items-end">
        <form className="flex items-end space-x-3" onSubmit={handleSubmitForm}>
          <div className="relative" style={{ width: '16rem' }}>
            <FormLabel htmlFor="invoice-input">Nomor Invoice:</FormLabel>
            <FormInput
              id="invoice-input"
              placeholder="Masukkan nomor invoice..."
              style={{ marginBottom: 0 }}
              name="invoiceNumber"
              value={forms.invoiceNumber}
              onChange={handleInputChange}
            />
          </div>
          <div className="relative" style={{ width: '16rem' }}>
            <FormLabel htmlFor="supplier-input">Nama Distributor:</FormLabel>
            <FormInput
              id="supplier-input"
              placeholder="Masukkan nama distributor..."
              style={{ marginBottom: 0 }}
              autoComplete="off"
              name="distributor"
              value={forms.distributor.name}
              onChange={handleInputChange}
            />
            <Autocomplete
              value={selectedSupplierSuggestion}
              options={suggestionOptions}
              onChange={(value) => handleSelectSuggestion(value)}
            />
          </div>
          <div className="relative">
            <FormLabel htmlFor="entry-date-input">Tanggal Masuk:</FormLabel>
            <FormInput
              type="date"
              id="entry-date-input"
              max={toISODateInput(getInitialDate('daily'))}
              name="entryDate"
              value={forms.entryDate}
              onChange={handleInputChange}
            />
          </div>
          <Button type="submit" variant="primary" disabled={hasFormErrors}>
            <FaSearch className="mr-2" /> Cari
          </Button>
        </form>

        <div className="ml-auto flex items-center space-x-4">
          <Button variant="success" onClick={onAddProductClicked}>
            <FaPlusCircle className="mr-2" /> Tambah Invoice Baru
          </Button>
        </div>
      </div>

      {hasFormErrors && (
        <ul className="pl-4 my-0">
          {Object.values(formErrors).map((error, idx) => (
            error ? <li key={idx} className="my-0 text-sm text-error">{error}</li> : null
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProductUnitHeader;
